
!function () {
  "use strict";

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('[data-product-form]');
      this.formWrapper = (theme.settings.productPageSticky && this.section?.querySelector('[data-form-wrapper]')) || this.form;

      // Botón principal del form (fuente de verdad)
      this.mainSubmit = this.form?.querySelector('[data-add-to-cart]') || this.form?.querySelector('button[type="submit"]');

      // Botones del sticky/cart-bar a sincronizar (dentro del cart-bar y/o container-sticky)
      const stickyCandidates = [
        ...this.querySelectorAll('[data-cart-bar-add-to-cart]'),
        ...((this.section || document).querySelectorAll('.container-sticky .product__submit__item button') || [])
      ];
      // Dejar únicos
      this.stickyButtons = Array.from(new Set(stickyCandidates)).filter(btn => !!btn);

      // Guards de binds
      this.handleProductAddError = this.handleProductAddError.bind(this);
      this.boundToggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
      this.boundSetCartBarHeight = this.setCartBarHeight.bind(this);
      this.syncFromMain = this.syncFromMain.bind(this);
      this._onVariantEvent = this._onVariantEvent.bind(this);

      this._stickyOriginalTextMap = new WeakMap();   // btn -> label original
      this._mainSubmitObserver = null;
      this._variantIdObserver = null;
    }

    connectedCallback() {
      this.setCartBarHeight();
      this.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('theme:scroll', this.boundToggleCartBarOnScroll);
      document.addEventListener('theme:resize', this.boundSetCartBarHeight);
      document.addEventListener('theme:product:add-error', this.handleProductAddError);

      // Guardar textos originales de sticky(s)
      this.stickyButtons.forEach((btn) => {
        const labelNode = btn.querySelector('[data-add-to-cart-text]') || btn;
        const txt = (labelNode.textContent || '').trim() || 'Agregar al carrito';
        this._stickyOriginalTextMap.set(btn, txt);
      });

      // Observar cambios en el submit principal (disabled / texto cambiante del tema)
      this._attachObservers();

      // Escuchar eventos comunes de cambio de variante
      ['theme:variant:change','variant:change','product:variant-change','shopify:variant:change']
        .forEach(evt => (this.section || document).addEventListener(evt, this._onVariantEvent, { passive: true }));

      // Primer sincronizado
      this.syncFromMain();
      setTimeout(this.syncFromMain, 120);
      setTimeout(this.syncFromMain, 400);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product:add-error', this.handleProductAddError);
      document.removeEventListener('theme:scroll', this.boundToggleCartBarOnScroll);
      document.removeEventListener('theme:resize', this.boundSetCartBarHeight);

      ['theme:variant:change','variant:change','product:variant-change','shopify:variant:change']
        .forEach(evt => (this.section || document).removeEventListener(evt, this._onVariantEvent));

      if (this._mainSubmitObserver) this._mainSubmitObserver.disconnect();
      if (this._variantIdObserver) this._variantIdObserver.disconnect();
    }

    handleClick(ev) {
      const addBtn = ev.target.closest('[data-cart-bar-add-to-cart]');
      if (addBtn) {
        ev.preventDefault();
        addBtn.classList.add('is-loading');
        addBtn.setAttribute('disabled','disabled');
        // Click programático al botón real del form
        const real = this.form?.querySelector('[data-add-to-cart]') || this.form?.querySelector('button[type="submit"]');
        if (real) real.dispatchEvent(new Event('click', { bubbles: true }));
        return;
      }

      if (ev.target.closest('[data-cart-bar-scroll]')) {
        ev.preventDefault();
        this.scrollToTop();
      }
    }

    handleProductAddError() {
      const btn = this.querySelector('[data-cart-bar-add-to-cart]');
      if (btn) this.scrollToTop();
    }

    scrollToTop() {
      const variantSelects = this.section?.querySelector('variant-selects');
      const target = (window.theme.isMobile() ? (variantSelects || this.form) : this.section);
      const top = target?.getBoundingClientRect().top || 0;
      window.theme.scrollTo(window.theme.isMobile() ? top - 10 : top);
    }

    toggleCartBarOnScroll() {
      const y = window.scrollY;
      if (this.formWrapper) {
        const past = y > (this.formWrapper.offsetTop + this.formWrapper.offsetHeight);
        this.classList.toggle('is-visible', past);
      }
    }

    setCartBarHeight() {
      const h = this.offsetHeight;
      document.documentElement.style.setProperty('--cart-bar-height', `${h}px`);
    }

    /** ─────────────────────────────
     *  SYNC: botón sticky ↔ botón form
     *  ───────────────────────────── */
    _attachObservers() {
      // Observa cambios de estado del botón principal
      if (this.mainSubmit && !this._mainSubmitObserver) {
        this._mainSubmitObserver = new MutationObserver(() => this.syncFromMain());
        this._mainSubmitObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true
        });
      }

      // Observa cambios en el input hidden de variante (para detectar cambio de variante)
      const idInput = this.form?.querySelector('[name="id"]');
      if (idInput && !this._variantIdObserver) {
        this._variantIdObserver = new MutationObserver(() => this.syncFromMain());
        this._variantIdObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    _onVariantEvent() {
      // Pequeño delay por si el tema actualiza disabled/labels async
      setTimeout(this.syncFromMain, 0);
    }

    syncFromMain() {
      if (!this.mainSubmit) return;
    
      // Nodo de texto del botón principal
      const mainLabelNode = this.mainSubmit.querySelector('[data-add-to-cart-text]') || this.mainSubmit;
      const mainLabelText = (mainLabelNode.textContent || '').trim();
      const mainLabelLower = mainLabelText.toLowerCase();
    
      const isDisabled =
        !!this.mainSubmit.disabled ||
        this.mainSubmit.getAttribute?.('aria-disabled') === 'true';
    
      // Solo consideramos "agotado" si está deshabilitado Y el texto lo indica
      const isSoldOut = isDisabled && mainLabelLower.includes('agotado');
    
      if (isSoldOut) {
        // Variante realmente agotada → sticky también en "Agotado"
        const soldOutText = mainLabelText || 'Agotado';
        this._setStickyState(true, soldOutText);
      } else {
        // No está agotado:
        // - Puede estar cargando (disabled pero texto "Agregando..." / "Añadiendo")
        // - O estar normal y listo para agregar
        this.stickyButtons.forEach((btn) => {
          const original = this._stickyOriginalTextMap.get(btn) || 'Agregar al carrito';
    
          // Si quieres que el sticky también se bloquee mientras el botón principal está "loading",
          // puedes usar una clase del tema si existe:
          const mainIsLoading = this.mainSubmit.classList.contains('is-loading');
    
          // Aquí decides: si quieres que el sticky quede bloqueado mientras carga, usa `mainIsLoading`.
          // Si quieres que se comporte completamente independiente de ese estado, pon `false`.
          const disabled = mainIsLoading ? true : false;
    
          this._applyButtonState(btn, disabled, original);
        });
      }
    }

    _setStickyState(disabled, text) {
      this.stickyButtons.forEach(btn => this._applyButtonState(btn, disabled, text));
    }

    _applyButtonState(btn, disabled, text) {
      btn.toggleAttribute('disabled', !!disabled);
      btn.classList.toggle('is-disabled', !!disabled);
      btn.classList.toggle('disabled', !!disabled);
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      const labelNode = btn.querySelector('[data-add-to-cart-text]') || btn;
      const priceNode = labelNode.querySelector?.('[data-product-price]');
      if (priceNode) {
        const html = priceNode.outerHTML;
        labelNode.textContent = text;
        labelNode.insertAdjacentHTML('beforeend', ` ${html}`);
      } else {
        labelNode.textContent = text;
      }
    }
  }

  customElements.get('cart-bar') || customElements.define('cart-bar', CartBar);
}();
